import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, RefreshCw, Loader2, ExternalLink, AlertTriangle, Microscope,
  Award, Eye, Telescope, FlaskConical, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { adminRadarList, adminRadarScan, adminRadarAction } from "../../lib/api";

const URGENCY_META = {
  breaking:   { label: "BREAKING",   color: "#D92D20" },
  developing: { label: "DEVELOPING", color: "#E0A526" },
  standing:   { label: "EVERGREEN",  color: "#A39E93" },
  cold:       { label: "COLD",       color: "#757065" },
};
const SCORE_COLORS = {
  high:   { color: "#5DD4B3", border: "#5DD4B3" },
  medium: { color: "#CBB892", border: "#CBB892" },
  low:    { color: "#757065", border: "#757065" },
};
// High-value source classes get an elevated visual treatment.
const HIGH_VALUE_CLASSES = new Set([
  "investigative_journalism",
  "primary_documents",
  "government_records",
  "court_or_filings",
  "declassified_archives",
  "whistleblower_leaks",
]);
const SOURCE_CLASS_LABEL = {
  investigative_journalism: "INVESTIGATIVE JOURNALISM",
  primary_documents: "PRIMARY DOCUMENTS",
  government_records: "GOV / OFFICIAL RECORDS",
  court_or_filings: "COURT FILINGS",
  declassified_archives: "DECLASSIFIED ARCHIVE",
  whistleblower_leaks: "WHISTLEBLOWER / LEAK",
  standard_news: "STANDARD NEWS",
  academic: "ACADEMIC",
  think_tank: "THINK TANK",
  book: "BOOK",
  blog_commentary: "BLOG / COMMENTARY",
};

const TABS = [
  { key: "new", label: "New" },
  { key: "monitoring", label: "Monitoring" },
  { key: "promoted", label: "Promoted" },
  { key: "duplicate", label: "Duplicates" },
  { key: "ignored", label: "Ignored" },
  { key: "archived", label: "Archived" },
];

const ScoreChip = ({ label, value, icon: Icon }) => (
  <div className="flex flex-col items-start min-w-0">
    <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#757065] flex items-center gap-1">
      {Icon && <Icon size={9} />} {label}
    </span>
    <span className="font-display text-base text-[#E4DFD3] leading-none">
      {value === null || value === undefined ? "—" : value}
      <span className="text-[#757065] text-[10px]">/10</span>
    </span>
  </div>
);

export const AdminNewsRadar = ({ token, userToken = null }) => {
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastScan, setLastScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token && !userToken) return;
    setLoading(true);
    try {
      const r = await adminRadarList(token, tab, userToken);
      setItems(r.items || []); setCounts(r.counts || {}); setLastScan(r.last_scan || null);
    } catch { toast.error("Failed to load radar"); }
    finally { setLoading(false); }
  }, [token, userToken, tab]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (lastScan?.status !== "running") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [lastScan, load]);

  const runScan = async () => {
    setScanning(true);
    try {
      await adminRadarScan(token, [], userToken);
      toast.success("Radar scan started — items will appear in a moment.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const act = async (rid, action) => {
    const note = (action === "ignore" || action === "duplicate" || action === "archive")
      ? null : window.prompt(`Optional note for "${action}":`, "") || null;
    try {
      const r = await adminRadarAction(token, rid, action, note, userToken);
      if (r.draft_id) toast.success("Investigation started — moved to Review Folder · Investigating");
      else if (r.submission_id) toast.success(`Saved as lead — ${r.submission_id}`);
      else toast.success(`Marked: ${r.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div data-testid="admin-news-radar">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Radio size={13} /> // NEWS RADAR
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">Wire Desk</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Lead-discovery & overlap detection. v1 runs on-demand using AI-generated
            candidates checked against existing dossiers.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={runScan} disabled={scanning} data-testid="radar-run-scan"
            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            {scanning ? <Loader2 className="animate-spin" size={12} /> : <Radio size={12} />} Run scan
          </button>
          <button onClick={load} data-testid="radar-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {lastScan && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 font-mono text-[10px] text-[#757065] flex flex-wrap gap-3">
          <span>last scan: <span className="text-[#A39E93]">{lastScan.status}</span></span>
          {lastScan.item_count !== undefined && <span>items: <span className="text-[#A39E93]">{lastScan.item_count}</span></span>}
          {lastScan.started_at && <span>started: {new Date(lastScan.started_at).toLocaleString()}</span>}
        </div>
      )}

      <div className="flex overflow-x-auto whitespace-nowrap gap-1 my-4 border-b border-[#2D2A26] -mx-1 px-1 lg:flex-wrap lg:overflow-visible lg:whitespace-normal">
        {TABS.map((t) => {
          const c = counts[t.key] || 0;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`radar-tab-${t.key}`}
              className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              {t.label}{c ? <span className="ml-1 text-[#757065]">·&nbsp;{c}</span> : null}
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-12 text-center font-mono text-xs uppercase tracking-widest text-[#757065]" data-testid="radar-empty">
          No radar items here. Run a scan to populate.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((it) => {
            const u = URGENCY_META[it.urgency] || URGENCY_META.standing;
            const rs = SCORE_COLORS[it.overall_radar_score] || SCORE_COLORS.medium;
            const elevated = HIGH_VALUE_CLASSES.has(it.source_class);
            const sourceLabel = SOURCE_CLASS_LABEL[it.source_class] || (it.source_class || "").replace(/_/g, " ").toUpperCase();
            return (
            <div
              key={it.id}
              data-testid={`radar-card-${it.id}`}
              className={`border bg-[#131313] p-4 sm:p-5 aged-paper relative ${
                elevated ? "border-[#D92D20]/60 shadow-[0_0_0_1px_rgba(217,45,32,0.15)]" : "border-[#2D2A26]"
              }`}
            >
              {elevated && (
                <div className="absolute -top-px -left-px font-mono text-[8px] uppercase tracking-[0.3em] bg-[#D92D20] text-white px-2 py-0.5">
                  HIGH-VALUE SOURCE
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mb-2 mt-2">
                <span style={{ color: u.color, borderColor: u.color }}
                      className="inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
                      data-testid={`radar-urgency-${it.id}`}>
                  {u.label}
                </span>
                <span
                  data-testid={`radar-source-class-${it.id}`}
                  className={`inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em] ${
                    elevated
                      ? "text-[#D92D20] border-[#D92D20]/70 bg-[#1a0808]"
                      : "text-[#A39E93] border-[#454038]"
                  }`}
                >
                  {sourceLabel}
                </span>
                <span style={{ color: rs.color, borderColor: rs.border }}
                      className="inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
                      data-testid={`radar-overall-${it.id}`}>
                  RADAR: {(it.overall_radar_score || "medium").toUpperCase()}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
                  CAT: {it.category}
                </span>
                {it.uncertain_url && (
                  <span className="inline-flex items-center gap-1 font-mono text-[9px] text-[#E0A526] border border-[#E0A526]/40 px-2 py-0.5">
                    <AlertTriangle size={9} /> URL UNCERTAIN
                  </span>
                )}
              </div>
              <h3 className="font-heading text-lg sm:text-xl text-[#E4DFD3] mb-1 leading-tight">{it.title}</h3>
              <p className="text-sm text-[#A39E93] mb-2 line-clamp-3">{it.summary}</p>
              {it.ai_recommendation && (
                <div className="text-xs text-[#E4DFD3] mb-2 border-l-2 border-[#5DD4B3] pl-2">
                  <span className="font-mono text-[9px] text-[#5DD4B3] uppercase tracking-[0.25em]">AI rec:</span>{" "}
                  {it.ai_recommendation}
                </div>
              )}
              <p className="text-xs italic text-[#CBB892] mb-3 border-l-2 border-[#D92D20] pl-2">
                Why flagged: {it.why_flagged}
              </p>

              {/* Score grid */}
              <div className="grid grid-cols-5 gap-2 mb-3 border-y border-[#2D2A26] py-2">
                <ScoreChip label="FIT" value={it.archive_fit_score} icon={Award} />
                <ScoreChip label="SOURCE" value={it.source_strength_score} icon={Sparkles} />
                <ScoreChip label="FRESH" value={it.freshness_score} icon={Radio} />
                <ScoreChip label="ORIG" value={it.originality_score} icon={Eye} />
                <ScoreChip label="INVEST" value={it.investigation_potential_score} icon={Telescope} />
              </div>

              {/* Editorial signals */}
              {(it.suggested_angle || it.fit_rationale || it.next_evidence_to_chase) && (
                <div className="border border-[#2D2A26] bg-[#0a0a0a] px-3 py-2 mb-3 text-xs text-[#A39E93] space-y-1">
                  {it.suggested_angle && (
                    <div><span className="font-mono text-[9px] text-[#D92D20] uppercase tracking-[0.25em] mr-2">ANGLE:</span>{it.suggested_angle}</div>
                  )}
                  {it.fit_rationale && (
                    <div><span className="font-mono text-[9px] text-[#D92D20] uppercase tracking-[0.25em] mr-2">FIT:</span>{it.fit_rationale}</div>
                  )}
                  {it.next_evidence_to_chase && (
                    <div className="flex items-start gap-2">
                      <FlaskConical size={11} className="text-[#9FA8D4] mt-0.5 shrink-0" />
                      <div><span className="font-mono text-[9px] text-[#9FA8D4] uppercase tracking-[0.25em] mr-2">NEXT:</span>{it.next_evidence_to_chase}</div>
                    </div>
                  )}
                  {it.suggested_action === "monitor" && it.monitor_triggers && (
                    <div className="text-[#CBB892]"><span className="font-mono text-[9px] uppercase tracking-[0.25em] mr-2">MONITOR IF:</span>{it.monitor_triggers}</div>
                  )}
                </div>
              )}

              {it.primary_outlet && (
                <div className="font-mono text-[10px] text-[#757065] mb-2 break-all">
                  {it.primary_outlet}
                  {it.primary_url && (
                    <a href={it.primary_url} target="_blank" rel="noopener noreferrer"
                       className="ml-2 inline-flex items-center gap-1 text-[#A39E93] hover:text-[#D92D20]">
                      lead URL <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              )}
              {it.overlap_candidates?.length > 0 && (
                <div className="border border-[#CBB892]/30 bg-[#1a1408] px-2 py-1.5 mb-3">
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#CBB892] mb-1">
                    OVERLAP CANDIDATES
                  </div>
                  {it.overlap_candidates.map((oc, i) => (
                    <div key={i} className="text-xs text-[#A39E93]">
                      → {oc.slug ? <Link to={`/topic/${oc.slug}`} target="_blank" className="text-[#E4DFD3] hover:text-[#D92D20]">{oc.title}</Link> : oc.title} — {oc.reason}
                    </div>
                  ))}
                </div>
              )}

              {/* Primary actions (Investigate / Lead / Monitor / Ignore) — stack on mobile */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                <button onClick={() => act(it.id, "investigate")} data-testid={`radar-investigate-${it.id}`}
                  className="col-span-2 sm:col-auto sm:flex-1 inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  <Microscope size={11} /> Investigate
                </button>
                <button onClick={() => act(it.id, "lead")} data-testid={`radar-lead-${it.id}`}
                  className="inline-flex items-center justify-center gap-2 border border-[#5DD4B3]/40 hover:bg-[#0a1612] text-[#5DD4B3] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Save as lead
                </button>
                <button onClick={() => act(it.id, "monitor")} data-testid={`radar-monitor-${it.id}`}
                  className="inline-flex items-center justify-center gap-2 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Monitor
                </button>
                <button onClick={() => act(it.id, "ignore")} data-testid={`radar-ignore-${it.id}`}
                  className="inline-flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#757065] hover:text-[#D92D20] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Dismiss
                </button>
              </div>

              {/* Secondary action — Duplicate (less common, smaller) */}
              <div className="mt-2 text-right">
                <button onClick={() => act(it.id, "duplicate")} data-testid={`radar-dup-${it.id}`}
                  className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#CBB892]">
                  mark duplicate →
                </button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

export default AdminNewsRadar;
